import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useSetPlanningIncomeMutation } from '../../features/budgets/queries';
import pt from '../../locales/pt';
import { extractMutationError } from '../../utils/apiError';
import { parsePtNumber } from '../../utils/money';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { NumericInput } from '../common/NumericInput';

function amountToDraft(value) {
  if (value == null || Number(value) <= 0) return '';
  return String(value).replace('.', ',');
}

function draftMatchesAmount(draft, amount) {
  const numeric = parsePtNumber(draft);
  if (numeric === null || amount <= 0) return false;
  return Math.abs(numeric - amount) < 0.005;
}

/**
 * Editar rendimento usado no planeamento do orçamento.
 */
export function EditPlanningIncomeDialog({ isOpen, onClose, overview }) {
  const setPlanningIncome = useSetPlanningIncomeMutation();
  const [amountDraft, setAmountDraft] = useState('');

  const cycleIncome = Number(overview?.cycle_income_eur ?? 0);
  const registeredIncome = Number(overview?.registered_cycle_income_eur ?? 0);
  const previousIncome = Number(overview?.previous_cycle_income_eur ?? 0);
  const activeMode = overview?.planning_income_mode ?? 'previous_cycle';

  useEffect(() => {
    if (!isOpen) return;
    const seed =
      activeMode === 'manual' && overview?.planning_income_override_eur != null
        ? overview.planning_income_override_eur
        : cycleIncome;
    setAmountDraft(
      seed !== null && seed !== undefined && Number(seed) > 0
        ? amountToDraft(seed)
        : '',
    );
  }, [
    isOpen,
    activeMode,
    cycleIncome,
    overview?.planning_income_override_eur,
  ]);

  const applyMode = (mode, overrideEur = null) => {
    const payload = { planningIncomeMode: mode };
    if (mode === 'manual' && overrideEur != null) {
      payload.planningIncomeOverrideEur = overrideEur;
    }
    setPlanningIncome.mutate(payload, {
      onSuccess: () => {
        toast.success(pt.budget.planningIncome.saved);
        onClose();
      },
      onError: (err) => {
        toast.error(extractMutationError(err, pt.budget.planningIncomeSaveError));
      },
    });
  };

  const handleSave = () => {
    const numeric = parsePtNumber(amountDraft);
    if (numeric === null || numeric <= 0) {
      toast.error(pt.budget.manualIncomeInvalid);
      return;
    }

    const rounded = numeric.toFixed(2);
    if (
      previousIncome > 0
      && Math.abs(numeric - previousIncome) < 0.005
    ) {
      applyMode('previous_cycle');
      return;
    }
    if (
      registeredIncome > 0
      && Math.abs(numeric - registeredIncome) < 0.005
    ) {
      applyMode('current_cycle');
      return;
    }
    applyMode('manual', rounded);
  };

  const chips = [
    previousIncome > 0
      ? {
          id: 'previous',
          label: pt.budget.planningIncome.chipPrevious,
          amount: previousIncome,
        }
      : null,
    registeredIncome > 0
      ? {
          id: 'current',
          label: pt.budget.planningIncome.chipCurrent,
          amount: registeredIncome,
        }
      : null,
  ].filter(Boolean);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{pt.budget.planningIncome.editTitle}</DialogTitle>
          <DialogDescription>{pt.budget.planningIncome.editDescription}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted-foreground">
              {pt.budget.manualIncomeLabel}
            </label>
            <NumericInput
              value={amountDraft}
              onChange={(event) => setAmountDraft(event.target.value)}
              placeholder={pt.budget.manualIncomePlaceholder}
            />
          </div>

          {chips.length > 0 ? (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={pt.budget.planningIncome.chipsLabel}>
              {chips.map((chip) => {
                const selected = draftMatchesAmount(amountDraft, chip.amount);
                return (
                  <button
                    key={chip.id}
                    type="button"
                    aria-pressed={selected}
                    disabled={setPlanningIncome.isPending}
                    className={cn(
                      'rounded-ds-full px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm',
                      selected
                        ? 'bg-sage-700 text-white shadow-sm'
                        : 'bg-muted text-muted-foreground hover:bg-stone-200',
                    )}
                    onClick={() => setAmountDraft(amountToDraft(chip.amount))}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {pt.common.cancel}
            </Button>
            <Button
              type="button"
              disabled={setPlanningIncome.isPending}
              onClick={handleSave}
            >
              {pt.budget.manualIncomeSave}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
